import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  MoreHorizontal, Check, X, Trash2, Eye, Shield, Edit, Plus,
  RefreshCw, Archive, Search, Filter,
  Loader2
} from 'lucide-react';
import {
  getAdminGigs, softDeleteGig, hardDeleteGig, restoreGig, toggleActive, duplicateGig
} from '../../services/gig.service';
import { format } from 'date-fns';

// ── Sub-components ─────────────────────────────────────────────────────────────
const CustomCheckbox = ({ checked, onChange, indeterminate = false }) => (
  <div
    className={`w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${
      checked || indeterminate ? 'bg-[#4B718F] border-[#4B718F]' : 'border-gray-300 dark:border-[#32353c] bg-white dark:bg-inherit'
    }`}
    onClick={onChange}
  >
    {(checked || indeterminate) && <Check className="w-3 h-3 text-white" />}
  </div>
);

const StatusBadge = ({ isActive, status, deletedAt }) => {
  if (deletedAt) return <span className="px-2 py-0.5 rounded-full text-xs border border-red-400 dark:border-red-500/50 text-red-500 dark:text-red-400">Trashed</span>;
  return (
    <div className="flex flex-col gap-1">
      <span className={`px-2 py-0.5 rounded-full text-[9px]  ${isActive ? 'text-green-600 dark:text-green-400 dark:border-green-500/50' : 'text-gray-500 dark:text-gray-400 dark:border-gray-600'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
      <span className={`px-2 py-0.5 rounded-full text-[9px] ${status === 'published' ? 'text-blue-500 dark:text-blue-400 dark:border-blue-500/50' : 'text-yellow-600 dark:text-yellow-500 dark:border-yellow-500/50'}`}>
        {status === 'published' ? 'Published' : 'Draft'}
      </span>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const GigsTable = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({ status: 'all', isActive: 'all', trash: false, search: '' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const [showRowDropdown, setShowRowDropdown] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { action, gig }

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const queryParams = {
    ...(filters.trash && { trash: 'true' }),
    limit: 100, // Fetch a large batch for client-side filtering
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminGigs', { trash: filters.trash, page }],
    queryFn: () => getAdminGigs({ ...queryParams, page }),
  });

  const gigs = data?.gigs || [];

  // Client-side filtering
  const filteredGigs = gigs.filter(gig => {
    const matchesSearch = !filters.search || 
      gig.title.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || 
      gig.status === filters.status;
    
    const matchesActive = filters.isActive === 'all' || 
      String(gig.isActive) === String(filters.isActive);

    return matchesSearch && matchesStatus && matchesActive;
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['adminGigs'] });

  // ── Mutations ────────────────────────────────────────────────────────────────
  const softDelete = useMutation({
    mutationFn: (id) => softDeleteGig(id),
    onSuccess: () => { toast.success('Gig moved to trash'); invalidate(); setConfirmModal(null); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to delete'),
  });

  const hardDelete = useMutation({
    mutationFn: (id) => hardDeleteGig(id),
    onSuccess: () => { toast.success('Gig permanently deleted'); invalidate(); setConfirmModal(null); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to delete'),
  });

  const restore = useMutation({
    mutationFn: (id) => restoreGig(id),
    onSuccess: () => { toast.success('Gig restored'); invalidate(); setConfirmModal(null); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to restore'),
  });

  const toggleActiveM = useMutation({
    mutationFn: (id) => toggleActive(id),
    onSuccess: (data) => { toast.success(data.message); invalidate(); setConfirmModal(null); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed'),
  });

  const duplicate = useMutation({
    mutationFn: (id) => duplicateGig(id),
    onSuccess: (data) => {
      toast.success('Gig duplicated as draft');
      invalidate();
      setConfirmModal(null);
      navigate(`/admins/edit-gig/${data.gig._id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Failed to duplicate'),
  });

  // ── Bulk delete ───────────────────────────────────────────────────────────────
  const bulkSoftDelete = async (ids) => {
    await Promise.all(ids.map((id) => softDeleteGig(id)));
    toast.success(`${ids.length} gig(s) moved to trash`);
    setSelected([]);
    invalidate();
    setShowHeaderDropdown(false);
  };

  const bulkHardDelete = async (ids) => {
    await Promise.all(ids.map((id) => hardDeleteGig(id)));
    toast.success(`${ids.length} gig(s) permanently deleted`);
    setSelected([]);
    invalidate();
    setShowHeaderDropdown(false);
  };

  // ── Selection ────────────────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleSelectAll = () =>
    setSelected(selected.length === filteredGigs.length ? [] : filteredGigs.map((g) => g._id));

  const allSelected = filteredGigs.length > 0 && selected.length === filteredGigs.length;
  const isIndeterminate = selected.length > 0 && selected.length < filteredGigs.length;

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const hellocianNames = (gig) => {
    if (!gig.hellocians || gig.hellocians.length === 0) return '—';
    const names = gig.hellocians.slice(0, 2).map((h) => h.firstName || 'Hellocian').join(', ');
    return gig.hellocians.length > 2 ? `${names} +${gig.hellocians.length - 2} other` : names;
  };

  // ── Confirm modal actions ─────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!confirmModal) return;
    const { action, gig } = confirmModal;
    if (action === 'softDelete') softDelete.mutate(gig._id);
    if (action === 'hardDelete') hardDelete.mutate(gig._id);
    if (action === 'restore') restore.mutate(gig._id);
    if (action === 'toggleActive') toggleActiveM.mutate(gig._id);
    if (action === 'duplicate') duplicate.mutate(gig._id);
  };

  const isPending = softDelete.isPending || hardDelete.isPending || restore.isPending ||
    toggleActiveM.isPending || duplicate.isPending;

  // ── Empty states ──────────────────────────────────────────────────────────────
  if (isLoading) return <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-12 flex flex-col items-center justify-center">
    <Loader2 className="w-12 h-12 text-[#4B718F] animate-spin mb-4" />
  </div>;

  if (isError) return <div className="p-8 text-center text-red-500">Failed to load gigs.</div>;

  return (
    <>
      {/* New Gig Button */}
      {!filters.trash && (
        <a
          href="/admins/new-gig"
          className="mb-3 mr-3 w-fit flex justify-end ml-auto border bg-[#174568] border-[#174568] text-sm text-white rounded py-1.5 px-4 hover:bg-[#0f3a4f]"
        >
          + New Gig
        </a>
      )}

      <div className="bg-white dark:bg-[#1d2026] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-[#4B718F]">
              {filters.trash ? '🗑 Trash' : 'Gigs'}
            </h2>

            {/* Header actions dropdown */}
            <div className="relative">
              <button onClick={() => setShowHeaderDropdown(!showHeaderDropdown)} className="p-1 bg-[#F4F7FE] dark:bg-[#191c22] rounded-full">
                <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
              </button>
              {showHeaderDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => selected.length > 0 && bulkSoftDelete(selected)}
                    disabled={selected.length === 0}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />Delete Selected ({selected.length})
                  </button>
                  {filters.trash ? (
                    <button
                      onClick={() => selected.length > 0 && bulkHardDelete(selected)}
                      disabled={selected.length === 0}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 text-red-600 dark:text-red-400 flex items-center gap-2 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />Delete All Permanently
                    </button>
                  ) : (
                    <button
                      onClick={() => gigs.length > 0 && bulkSoftDelete(gigs.map((g) => g._id))}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 flex items-center gap-2 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />Move All to Trash
                    </button>
                  )}
                  <button onClick={toggleSelectAll} className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4" />{allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Filter bar ── */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search gigs…"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md w-full focus:outline-none focus:ring-1 focus:ring-[#4B718F]"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>

            {/* Status filter */}
            <select
              className="text-sm border border-gray-200 dark:border-[#32353c] dark:bg-[#191c22] dark:text-white rounded-md px-2 py-1.5 focus:outline-none"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, trash: false }))}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            {/* Active filter */}
            <select
              className="text-sm border border-gray-200 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md px-2 py-1.5 focus:outline-none"
              value={filters.isActive}
              onChange={(e) => setFilters((f) => ({ ...f, isActive: e.target.value, trash: false }))}
            >
              <option value="all">All (Active/Inactive)</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* Trash toggle */}
            <button
              onClick={() => setFilters((f) => ({ ...f, trash: !f.trash, status: 'all', isActive: 'all' }))}
              className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border transition-colors ${
                filters.trash ? 'bg-red-50 dark:bg-red-900/20 border-red-400 text-red-600 dark:text-red-400' : 'border-gray-200 dark:border-[#32353c] text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              Trash
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        {gigs.length === 0 ? (
          <div className="p-12 text-center text-gray-400 dark:text-gray-500">
            <div className="text-5xl mb-3">{filters.trash ? '🗑️' : '💼'}</div>
            <p>{filters.trash ? 'Trash is empty.' : 'No gigs found.'}</p>
            {!filters.trash && (
              <a href="/admins/new-gig" className="mt-3 inline-block text-[#174568] font-semibold hover:underline">+ Add new gig</a>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-[#1d2026]/60">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <CustomCheckbox checked={allSelected} indeterminate={isIndeterminate} onChange={toggleSelectAll} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase">Hellocians</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase">Orders</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase whitespace-nowrap">No. of Clicks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase whitespace-nowrap">Date Added</th>
                  {filters.trash && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase whitespace-nowrap">Deleted At</th>
                  )}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#141414] divide-y divide-gray-100 dark:divide-gray-800">
                {filteredGigs.map((gig) => (
                  <tr key={gig._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <CustomCheckbox checked={selected.includes(gig._id)} onChange={() => toggleSelect(gig._id)} />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{gig.title}</td>
                    <td className="px-4 py-3 text-sm text-[#4B718F] dark:text-blue-400 whitespace-nowrap">{hellocianNames(gig)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{gig.sales ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{Math.floor(gig.clicks ?? 0)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={gig.isActive} status={gig.status} deletedAt={gig.deletedAt} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {gig.createdAt ? format(new Date(gig.createdAt), 'd MMM yyyy') : '—'}
                    </td>
                    {filters.trash && (
                      <td className="px-4 py-3 text-sm text-red-400 whitespace-nowrap">
                        {gig.deletedAt ? format(new Date(gig.deletedAt), 'd MMM yyyy') : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="relative">
                        <button
                          onClick={() => setShowRowDropdown(showRowDropdown === gig._id ? null : gig._id)}
                          className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                        >
                          <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                        </button>

                        {showRowDropdown === gig._id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-20">
                            {!filters.trash ? (
                              <>
                                <button
                                  onClick={() => { navigate(`/admins/gigs/${gig._id}`); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2 text-sm"
                                >
                                  View Details
                                </button>
                                <button
                                  onClick={() => { navigate(`/admins/edit-gig/${gig._id}`); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2 text-sm"
                                >
                                  <Edit className="w-4 h-4" />Edit Gig
                                </button>
                                <button
                                  onClick={() => { setConfirmModal({ action: 'duplicate', gig }); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2 text-sm"
                                >
                                  <Plus className="w-4 h-4" />Duplicate
                                </button>
                                <button
                                  onClick={() => { setConfirmModal({ action: 'toggleActive', gig }); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2 text-sm"
                                >
                                  <Shield className="w-4 h-4" />{gig.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                  onClick={() => { setConfirmModal({ action: 'softDelete', gig }); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 flex items-center gap-2 text-sm"
                                >
                                  <Trash2 className="w-4 h-4" />Move to Trash
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setConfirmModal({ action: 'restore', gig }); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-green-600 dark:text-green-400 flex items-center gap-2 text-sm"
                                >
                                  <RefreshCw className="w-4 h-4" />Restore
                                </button>
                                <button
                                  onClick={() => { setConfirmModal({ action: 'hardDelete', gig }); setShowRowDropdown(null); }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 flex items-center gap-2 text-sm"
                                >
                                  <Trash2 className="w-4 h-4" />Delete Permanently
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Confirm Modal ── */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#0b0e14] rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {confirmModal.action === 'duplicate' && 'Duplicate Gig'}
                {confirmModal.action === 'toggleActive' && (confirmModal.gig.isActive ? 'Deactivate Gig' : 'Activate Gig')}
                {confirmModal.action === 'softDelete' && 'Move to Trash'}
                {confirmModal.action === 'hardDelete' && 'Delete Permanently'}
                {confirmModal.action === 'restore' && 'Restore Gig'}
              </h3>
              <button onClick={() => setConfirmModal(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
              {confirmModal.action === 'duplicate' && `Create a draft copy of "${confirmModal.gig.title}"? You'll be taken to the edit page.`}
              {confirmModal.action === 'toggleActive' && (confirmModal.gig.isActive
                ? `Deactivate "${confirmModal.gig.title}"? It will be hidden from clients.`
                : `Activate "${confirmModal.gig.title}"? It will be visible to clients.`)}
              {confirmModal.action === 'softDelete' && `Move "${confirmModal.gig.title}" to trash? You can restore it within 30 days.`}
              {confirmModal.action === 'hardDelete' && `Permanently delete "${confirmModal.gig.title}"? This cannot be undone.`}
              {confirmModal.action === 'restore' && `Restore "${confirmModal.gig.title}" from trash?`}
            </p>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmModal(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isPending}
                className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-60 ${
                  confirmModal.action === 'hardDelete' || (confirmModal.action === 'toggleActive' && confirmModal.gig.isActive)
                    ? 'bg-red-600 hover:bg-red-700'
                    : confirmModal.action === 'restore'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-[#4B718F] hover:bg-[#3a5a75]'
                }`}
              >
                {isPending ? 'Processing…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside */}
      {(showHeaderDropdown || showRowDropdown) && (
        <div className="fixed inset-0 z-10" onClick={() => { setShowHeaderDropdown(false); setShowRowDropdown(null); }} />
      )}
    </>
  );
};

export default GigsTable;